/**
 * aiService.ts - Serviço do Professor Pobre (IA)
 * 
 * Este serviço integra com a API do Google Gemini para fornecer
 * explicações didáticas e assertivas para questões de concursos.
 */
import { auth } from './firebase';

interface Question {
  id: string;
  subject: string;
  topic?: string;
  text: string;
  options: { id: string; text: string }[];
  correctOptionId?: string;
  comments?: string;
  year?: number;
  banca?: string;
}

export const GEMINI_API_URL =
  import.meta.env.VITE_GEMINI_API_URL ||
  'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-lite:generateContent';
export const GEMINI_API_KEY = import.meta.env.VITE_GEMINI_API_KEY;
const FUNCTIONS_BASE = import.meta.env.VITE_FUNCTIONS_BASE_URL || 'https://us-central1-concurseiro-pobre.cloudfunctions.net/api';

/**
 * Prompt do Professor Pobre - Personalidade e estilo de resposta
 */
const PROFESSOR_POBRE_SYSTEM_PROMPT = `Você é o Professor Pobre, um professor de concursos públicos brasileiro especialista, direto e motivador.

PERSONALIDADE:
- Você é objetivo e vai direto ao ponto, sem enrolação
- Usa linguagem acessível, mas tecnicamente precisa
- É motivador e encoraja o aluno a continuar estudando
- Faz referências à "lei seca" e dicas práticas de memorização
- Usa emojis com moderação para tornar a explicação mais leve

ESTRUTURA DA RESPOSTA:
1. **Análise da Questão** (2-3 linhas): Explique o que a questão está cobrando
2. **Resposta Correta**: Indique a alternativa correta e explique POR QUE está correta
3. **Por que cada uma das outras está errada**: Liste TODAS as alternativas incorretas, uma a uma, e explique por que estão erradas. Considere a alternativa que o aluno escolheu (diga explicitamente por que ela está errada, se for o caso).
4. **Dica do Professor Pobre**: Uma dica prática de memorização ou macete
5. **Fundamento Legal** (quando aplicável): Cite o artigo de lei ou súmula relevante

REGRAS:
- SEMPRE explique todas as alternativas: diga por que a correta está certa e por que cada errada está errada, mesmo que o aluno tenha acertado.
- Se o aluno errou, destaque a opção escolhida ao explicar por que ela está errada.
- Seja CONCISO: máximo 400 palavras
- Seja ASSERTIVO: não use "talvez", "possivelmente", "pode ser"
- Seja DIDÁTICO: explique como se o aluno nunca tivesse visto o tema
- Seja PRÁTICO: foque no que cai em prova
- NUNCA invente informações - se não souber, diga que precisa verificar
- Use formatação Markdown para organizar a resposta`;

/**
 * Gera explicação do Professor Pobre para uma questão
 */
export const getProfessorPobreExplanation = async (
  question: Question,
  selectedOptionId: string,
  mode: 'question' | 'flashcard' = 'question'
): Promise<string> => {
  const isCorrect = selectedOptionId === question.correctOptionId;
  const selectedOption = question.options.find(o => o.id === selectedOptionId);
  const correctOption = question.options.find(o => o.id === question.correctOptionId);

  try {
    const idToken = await auth.currentUser?.getIdToken?.();
    const response = await fetch(`${FUNCTIONS_BASE}/ai/direct-comment`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      },
      body: JSON.stringify({
        question,
        selectedOptionId,
        mode,
        forceOptionAnalysis: true,
        studentAnswerCorrect: isCorrect,
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Erro na função Professor Pobre:', errorData);
      throw new Error(`Erro na IA: ${response.status} - ${errorData.error || 'Erro desconhecido'}`);
    }

    const data = await response.json();
    if (!data?.comment) {
      throw new Error('Resposta inválida da função Professor Pobre');
    }

    const base = data.comment as string;
    const alreadySigned = base.includes('Professor Pobre');
    if (alreadySigned) return base;

    const signature = isCorrect || mode === 'flashcard'
      ? '\n\n---\n🎓 **Professor Pobre** | Continue assim, futuro servidor! 💪'
      : '\n\n---\n🎓 **Professor Pobre** | Errar faz parte! O importante é aprender. 📚';
    
    return base + signature;

  } catch (error: any) {
    console.error('Erro ao chamar Professor Pobre:', error);
    
    // Fallback com explicação básica se a API falhar
    if (question.comments) {
      return `🤖 **Professor Pobre (Modo Offline)**\n\n${isCorrect ? '✅ Parabéns pelo acerto!' : '❌ Não foi dessa vez...'}\n\n**Explicação:**\n${question.comments}\n\n💡 **Dica:** Revise sempre a lei seca e faça muitas questões!\n\n---\n🎓 *Professor Pobre* | A IA está temporariamente indisponível, mas o conhecimento continua!`;
    }
    
    throw new Error(`Não foi possível obter a explicação: ${error.message}`);
  }
};

/**
 * Gera feedback do simulado
 */
export const getSimuladoFeedback = async (
  questions: Question[],
  answers: { [questionId: string]: boolean }
): Promise<string> => {
  
  const total = questions.length;
  const correct = Object.values(answers).filter(Boolean).length;
  const percentage = Math.round((correct / total) * 100);
  
  // Análise por matéria
  const bySubject: { [subject: string]: { total: number; correct: number } } = {};
  questions.forEach(q => {
    if (!bySubject[q.subject]) {
      bySubject[q.subject] = { total: 0, correct: 0 };
    }
    bySubject[q.subject].total++;
    if (answers[q.id]) {
      bySubject[q.subject].correct++;
    }
  });

  let feedback = `## 📊 Resultado do Simulado\n\n`;
  feedback += `**Você acertou ${correct} de ${total} questões (${percentage}%)**\n\n`;
  
  if (percentage >= 80) {
    feedback += `🚀 **Excelente!** Você está no caminho certo para a aprovação!\n\n`;
  } else if (percentage >= 60) {
    feedback += `⚖️ **Bom progresso!** Foque nos pontos fracos identificados abaixo.\n\n`;
  } else if (percentage >= 40) {
    feedback += `📚 **Precisa melhorar!** Revise os temas e refaça as questões erradas.\n\n`;
  } else {
    feedback += `💪 **Não desanime!** Todo concurseiro passa por isso. Revise a teoria e volte mais forte!\n\n`;
  }

  feedback += `### Desempenho por Matéria\n\n`;
  Object.entries(bySubject).forEach(([subject, stats]) => {
    const subjectPercentage = Math.round((stats.correct / stats.total) * 100);
    const emoji = subjectPercentage >= 70 ? '✅' : subjectPercentage >= 50 ? '⚠️' : '❌';
    feedback += `${emoji} **${subject}**: ${stats.correct}/${stats.total} (${subjectPercentage}%)\n`;
  });

  feedback += `\n---\n🎓 **Professor Pobre** | Cada simulado é um passo mais perto da aprovação!`;

  return feedback;
};

/**
 * Processa o texto bruto de um edital e retorna a estrutura JSON das matérias e a data da prova
 */
export const parseExamContent = async (rawText: string): Promise<{ subjects: any[]; examDate?: string; metadata?: any; info?: { truncated: boolean; usedChars: number; totalChars: number; chunks?: number; warnings?: string[] }; cargos?: any[]; canonical?: any }> => {
  if (!GEMINI_API_KEY) throw new Error('Chave da API Gemini não configurada');

  const normalizeText = (text: string) => text
    .replace(/\u00a0/g, ' ')
    .replace(/\r/g, '')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  const stripRepeatedLines = (text: string) => {
    const lines = text.split('\n').map((l) => l.trim());
    const freq: Record<string, number> = {};
    lines.forEach((l) => {
      if (!l) return;
      const key = l.toLowerCase();
      freq[key] = (freq[key] || 0) + 1;
    });
    return lines
      .filter((l) => {
        if (!l) return false;
        const low = l.toLowerCase();
        if (/^p[aá]gina\s+\d+/i.test(low)) return false;
        return (freq[low] || 0) < 8;
      })
      .join('\n');
  };

  const splitIntoChunks = (text: string, chunkSize = 18000, overlap = 1500) => {
    const chunks: string[] = [];
    let cursor = 0;
    while (cursor < text.length) {
      const end = Math.min(text.length, cursor + chunkSize);
      chunks.push(text.slice(cursor, end));
      if (end >= text.length) break;
      cursor = Math.max(0, end - overlap);
    }
    return chunks;
  };

  const extractJson = (raw: string): any | null => {
    if (!raw) return null;
    let s = String(raw).replace(/```json/gi, '').replace(/```/g, '').trim();
    const start = s.indexOf('{');
    const end = s.lastIndexOf('}');
    if (start === -1 || end === -1 || end <= start) return null;
    s = s.slice(start, end + 1);

    const parseSafe = (input: string) => {
      try { return JSON.parse(input); } catch { return null; }
    };

    let parsed = parseSafe(s);
    if (parsed) return parsed;

    let fixed = s;
    const quoteCount = (fixed.match(/"/g) || []).length;
    if (quoteCount % 2 !== 0) fixed += '"';
    const openBrackets = (fixed.match(/\[/g) || []).length;
    const closeBrackets = (fixed.match(/\]/g) || []).length;
    const openBraces = (fixed.match(/\{/g) || []).length;
    const closeBraces = (fixed.match(/\}/g) || []).length;
    for (let i = 0; i < openBrackets - closeBrackets; i++) fixed += ']';
    for (let i = 0; i < openBraces - closeBraces; i++) fixed += '}';
    return parseSafe(fixed);
  };

  const callGeminiJson = async (prompt: string, maxOutputTokens = 4096) => {
    const url = `${GEMINI_API_URL}?key=${GEMINI_API_KEY}`;
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contents: [{ role: 'user', parts: [{ text: prompt }] }],
        generationConfig: {
          temperature: 0.05,
          maxOutputTokens,
          responseMimeType: 'application/json',
        },
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Erro API ${response.status}: ${errorData?.error?.message || 'erro desconhecido'}`);
    }

    const data = await response.json();
    const raw = data?.candidates?.[0]?.content?.parts?.[0]?.text || '';
    const parsed = extractJson(raw);
    if (!parsed) throw new Error('IA não retornou JSON válido');
    return parsed;
  };

  const sanitized = stripRepeatedLines(normalizeText(rawText || ''));
  const totalChars = sanitized.length;
  if (!totalChars) throw new Error('Texto vazio após sanitização');

  const chunks = splitIntoChunks(sanitized);
  const warnings: string[] = [];

  const partials: any[] = [];
  for (let i = 0; i < chunks.length; i++) {
    const chunk = chunks[i];
    const prompt = `Você é um extrator jurídico de editais. Leia SOMENTE este chunk ${i + 1}/${chunks.length} e extraia com evidência.

Retorne APENAS JSON válido no formato:
{
  "metadataHints": {"institution": null, "banca": null, "year": null, "examDate": null},
  "cargos": [{"name": "", "requirements": [], "evidence": [{"quote": "", "chunk": ${i + 1}}]}],
  "subjects": [{"name": "", "topics": [""], "cargo": null, "confidence": "high|medium|low", "evidence": [{"quote": "", "chunk": ${i + 1}}]}],
  "rules": {"objective": null, "discursive": null, "physicalTest": null, "notes": []}
}

REGRAS ESTRITAS:
- Não invente nada fora do texto.
- Se um campo não aparecer, mantenha null/vazio.
- Quote de evidência precisa ser literal e curto (até 220 chars).
- Manter nomes de matérias e cargos como aparecem no edital.

CHUNK:
${chunk}`;

    try {
      const partial = await callGeminiJson(prompt, 3072);
      partials.push(partial);
    } catch (err: any) {
      warnings.push(`Falha no chunk ${i + 1}: ${err.message}`);
    }
  }

  if (partials.length === 0) {
    const manualSubjects = extractSubjectsManually(sanitized);
    if (manualSubjects.length > 0) {
      return {
        subjects: manualSubjects,
        examDate: null,
        metadata: null,
        info: { truncated: false, usedChars: totalChars, totalChars, chunks: chunks.length, warnings: ['Fallback manual acionado: extração por IA falhou em todos os chunks.'] },
      };
    }
    throw new Error('Falha total na extração por IA.');
  }

  const consolidatePrompt = `Você vai consolidar extrações parciais de um mesmo edital em uma versão canônica.
Retorne APENAS JSON válido no formato:
{
  "metadata": {"institution": null, "role": null, "banca": null, "year": null, "examDate": null},
  "cargos": [{"name": "", "requirements": [], "subjects": [""], "confidence": "high|medium|low"}],
  "subjects": [{"name": "", "topics": [""], "cargo": null, "confidence": "high|medium|low", "evidenceCount": 0}],
  "quality": {"globalConfidence": 0.0, "warnings": []}
}

REGRAS:
- Consolidar duplicados (matérias e cargos com nomes equivalentes).
- Não extrapolar fora das extrações.
- Garantir consistência cargo -> matérias.
- Manter até 40 matérias e 30 cargos se necessário.
- ` + 'Não omitir informação importante só para resumir.' + `

DADOS PARCIAIS:
${JSON.stringify(partials).slice(0, 800000)}`;

  const consolidated = await callGeminiJson(consolidatePrompt, 8192);

  const finalSubjects = Array.isArray(consolidated?.subjects)
    ? consolidated.subjects
      .map((s: any) => ({
        name: String(s?.name || '').trim(),
        topics: Array.isArray(s?.topics) ? s.topics.map((t: any) => String(t).trim()).filter(Boolean).slice(0, 20) : [],
        confidence: ['high', 'medium', 'low'].includes(s?.confidence) ? s.confidence : 'medium',
      }))
      .filter((s: any) => s.name)
      .slice(0, 60)
    : [];

  const resultSubjects = finalSubjects.length ? finalSubjects : extractSubjectsManually(sanitized);
  if (!finalSubjects.length) warnings.push('IA consolidada sem matérias; fallback manual aplicado.');

  return {
    subjects: resultSubjects,
    examDate: consolidated?.metadata?.examDate || null,
    metadata: consolidated?.metadata || null,
    cargos: Array.isArray(consolidated?.cargos) ? consolidated.cargos : [],
    canonical: consolidated,
    info: {
      truncated: false,
      usedChars: totalChars,
      totalChars,
      chunks: chunks.length,
      warnings: [...warnings, ...(consolidated?.quality?.warnings || [])],
    },
  };
};

/**
 * Extração manual básica de matérias quando a IA falha
 */
const extractSubjectsManually = (text: string): any[] => {
  const subjects: any[] = [];
  const foundSubjects = new Set<string>();
  
  // Lista de matérias conhecidas para buscar no texto
  const knownSubjects = [
    'Língua Portuguesa', 'Português',
    'Raciocínio Lógico', 'Matemática',
    'Informática', 'Noções de Informática',
    'Direito Constitucional',
    'Direito Administrativo',
    'Direito Penal',
    'Direito Processual Penal',
    'Direito Processual Civil',
    'Direito Civil',
    'Direito Tributário',
    'Direito do Trabalho',
    'Administração Pública',
    'Administração Geral',
    'Contabilidade',
    'Economia',
    'Legislação',
    'Ética',
    'Atualidades',
    'Conhecimentos Bancários',
    'Conhecimentos Específicos'
  ];
  
  const lowerText = text.toLowerCase();
  
  knownSubjects.forEach(subject => {
    if (lowerText.includes(subject.toLowerCase())) {
      foundSubjects.add(subject);
    }
  });
  
  foundSubjects.forEach(name => {
    subjects.push({
      name: name,
      topics: ['Edite os tópicos manualmente']
    });
  });
  
  return subjects.slice(0, 10);
};

export default {
  getProfessorPobreExplanation,
  getSimuladoFeedback,
  parseExamContent
};
